import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import random from 'lodash/random';

import Button from 'eventbrite_design_system/button/Button';
import IconButton from 'eventbrite_design_system/iconButton/IconButton';
import Illustration from 'eventbrite_design_system/illustration/Illustration';
import CrossChunkySvg from 'eventbrite_design_system/iconography/icons/CrossChunky';
import moment from 'js-utils/moment';
import gettext from 'js-utils/gettext';
import { MOMENT_DATE_FORMAT_STRING } from 'eventbrite_design_system/eventCard/constants';

import { constants as destinationConstants } from '@eb/destination-utils';
import { FOLLOW_CTA } from 'consumer-utils/constants';

import SuperCropOneSvg from '../resources/superCropOne';
import SuperCropTwoSvg from '../resources/superCropTwo';
import SuperCropThreeSvg from '../resources/superCropThree';

import { getUpcomingEventCopy } from 'consumer-utils/utils';

import './OrganizerCard.scss';

const { EVENTBRITE_CATEGORY_MAPPING } = destinationConstants;
const ORGANIZER_IMAGE_DIAMETER = '80px';
const SUPER_CROPS = [
    <SuperCropOneSvg key="supercrop-one" />,
    <SuperCropTwoSvg key="supercrop-two" />,
    <SuperCropThreeSvg key="supercrop-three" />,
];

const SampleEventAttachment = ({ startDate, name }) => (
    <div className="follow-page__organizer-card__sample-event eds-l-pad-hor-6 eds-l-pad-vert-4 eds-text-weight--heavy">
        <date className="follow-page__organizer-card__sample-event-date">
            {moment(startDate).format(MOMENT_DATE_FORMAT_STRING)}
        </date>
        <div>{name.toLocaleUpperCase()}</div>
    </div>
);

const OrganizerProfileImage = ({ imageUrl, organizerName }) => {
    const randomSuperCropImage = SUPER_CROPS[random(0, 2)];

    let organizerImage = null;

    if (imageUrl) {
        organizerImage = (
            <img
                className="organizer-card__image"
                src={imageUrl}
                width={ORGANIZER_IMAGE_DIAMETER}
                height={ORGANIZER_IMAGE_DIAMETER}
                alt={gettext(`${organizerName} profile image.`)}
            />
        );
    } else {
        organizerImage = (
            <div className="organizer-card__image-placeholder">
                <Illustration
                    type={randomSuperCropImage}
                    width={ORGANIZER_IMAGE_DIAMETER}
                    height={ORGANIZER_IMAGE_DIAMETER}
                />
            </div>
        );
    }

    return (
        <div className="organizer-card__image-container">{organizerImage}</div>
    );
};

export default class OrganizerCard extends React.PureComponent {
    static propTypes = {
        organizerId: PropTypes.string.isRequired,
        organizerName: PropTypes.string.isRequired,
        handlePopOrganizer: PropTypes.func.isRequired,
        handleFollowOrganizer: PropTypes.func.isRequired,
        trackGAEvent: PropTypes.func.isRequired,
        categoryId: PropTypes.string,
        imageUrl: PropTypes.string,
        url: PropTypes.string,
        handleExcludeOrganizer: PropTypes.func,
        handleViewOrganizer: PropTypes.func,
        handleSetError: PropTypes.func,
        type: PropTypes.string,
        label: PropTypes.string,
        // eslint-disable-next-line react/forbid-prop-types
        sampleEvent: PropTypes.object,
        numUpcomingEvents: PropTypes.number,
    };

    state = {
        isFollowing: false,
        isClosed: false,
    };

    componentWillUnmount() {
        if (this._followTimeout) {
            clearTimeout(this._followTimeout);
            this._followTimeout = null;
        }

        if (this._closeTimeout) {
            clearTimeout(this._closeTimeout);
            this._closeTimeout = null;
        }
    }

    _handleFollowClick = () => {
        const {
            categoryId,
            organizerId,
            handlePopOrganizer,
            handleFollowOrganizer,
            trackGAEvent,
            label,
        } = this.props;

        trackGAEvent({
            action: 'FollowOrganizer',
            label,
        });

        this.setState({ isFollowing: true });

        handleFollowOrganizer(organizerId).then(() => {
            this._followTimeout = setTimeout(() => {
                handlePopOrganizer({ categoryId, organizerId });
            }, 500);
        });
    };

    _handleClose = () => {
        const { handlePopOrganizer, organizerId, categoryId } = this.props;

        this.setState({ isClosed: true });

        this._closeTimeout = setTimeout(() => {
            handlePopOrganizer({ categoryId, organizerId });
        }, 500);
    };

    _handleOnClick = () => {
        const { organizerId, handleViewOrganizer } = this.props;

        if (handleViewOrganizer) {
            handleViewOrganizer(organizerId);
        }
    };

    render() {
        const {
            organizerId,
            imageUrl,
            url,
            organizerName,
            categoryId,
            type,
            sampleEvent,
            numUpcomingEvents,
        } = this.props;

        const { isFollowing, isClosed } = this.state;

        const organizerCardClasses = classnames(
            'organizer-card',
            {
                'organizer-card--grid eds-l-pad-hor-3 eds-l-pad-top-6 eds-l-pad-bot-5':
                    type === 'grid',
                'eds-l-pad-all-6': type !== 'grid',
            },
            'eds-bg-color--background',
        );

        const containerClasses = classnames({
            'eds-fx--fade-out': isFollowing || isClosed,
        });

        let dataPointOne = null;

        if (numUpcomingEvents) {
            let dataPointText = `${numUpcomingEvents} ${getUpcomingEventCopy(
                numUpcomingEvents,
            )}`;

            if (numUpcomingEvents < 5) {
                const categoryName = EVENTBRITE_CATEGORY_MAPPING[categoryId];

                dataPointText = `${gettext('Popular in')} ${categoryName}`;
            }

            dataPointOne = (
                <div className="organizer-card__subtitle-container">
                    <p className="eds-text-bm eds-text-color--ui-500">
                        {dataPointText}
                    </p>
                </div>
            );
        }

        return (
            <div className={containerClasses}>
                <section key={organizerId} className={organizerCardClasses}>
                    <div className="organizer-card__image_and_text">
                        <a
                            onClick={this._handleOnClick}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="organizer-card__link"
                            title={gettext(`View ${organizerName} profile.`)}
                            tabIndex="-1"
                        >
                            <OrganizerProfileImage
                                imageUrl={imageUrl}
                                organizerName={organizerName}
                            />
                        </a>

                        <a
                            onClick={this._handleOnClick}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="organizer-card__link"
                            title={gettext(`View ${organizerName} profile.`)}
                        >
                            <div className="organizer-card__title-container eds-l-mar-left-6 eds-l-mar-vert-1">
                                <h4 className="organizer-card__title eds-text-bl eds-text-color--ui-800">
                                    {organizerName}
                                </h4>
                            </div>
                            {dataPointOne}
                        </a>
                    </div>
                    <Button
                        __containerClassName="organizer-card__cta"
                        style="follow"
                        isFollowing={isFollowing}
                        onClick={this._handleFollowClick}
                    >
                        {FOLLOW_CTA}
                    </Button>

                    <div className="organizer-card__underlay">
                        <div className="organizer-card__close-btn">
                            <IconButton
                                iconType={<CrossChunkySvg />}
                                iconColor="grey-600"
                                title={gettext('Remove this organizer card')}
                                onClick={this._handleClose}
                            />
                        </div>
                    </div>
                </section>
                {sampleEvent && (
                    <SampleEventAttachment
                        startDate={sampleEvent.start_date}
                        name={sampleEvent.name}
                    />
                )}
            </div>
        );
    }
}
